// app/ai-use-cases/client-side-use-cases.tsx
"use client";

import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useRef, useState } from "react";

import { useClientReady } from "@/app/shared/hooks/useClientReady";
import { Button } from "@/components/ui/button";
import { UseCaseCard } from "@/components/use-case-list/use-case-card";
import { UseCaseFilter } from "@/components/use-case-list/use-case-filter";
import { UseCaseListItem } from "@/types/use-case.interface";

import {
  CLIENT_SIDE_USE_CASES_QUERY_KEY,
  CLIENT_SIDE_USE_CASES_REFETCH_INTERVAL_MS,
} from "./clientSideUseCasesQueryPolicy";
import { filterUseCases } from "./use-case-utils";

// Define FilterState locally as it's specific to this component's UI
interface FilterState {
  industries: string[];
  models: string[];
  useCaseTypes: string[];
}

interface ClientSideUseCasesProps {
  initialUseCases: UseCaseListItem[];
  industryOptions: string[];
  modelOptions: string[];
  useCaseTypeOptions: string[];
}

async function fetchPublicUseCases(
  signal?: AbortSignal,
): Promise<UseCaseListItem[]> {
  const response = await fetch("/api/ai-use-cases", { signal });
  if (!response.ok) {
    throw new Error(`Failed to load use cases (${response.status})`);
  }
  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error("Invalid use cases response");
  }
  return data as UseCaseListItem[];
}

export function ClientSideUseCases({
  initialUseCases,
  industryOptions,
  modelOptions,
  useCaseTypeOptions,
}: ClientSideUseCasesProps) {
  const initialUpdatedAtRef = useRef(Date.now());
  const {
    data: useCases = initialUseCases,
    isFetching: isLoading,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: CLIENT_SIDE_USE_CASES_QUERY_KEY,
    queryFn: ({ signal }) => fetchPublicUseCases(signal),
    initialData: initialUseCases,
    initialDataUpdatedAt: initialUpdatedAtRef.current,
    refetchInterval: CLIENT_SIDE_USE_CASES_REFETCH_INTERVAL_MS,
  });
  const lastRefreshed = new Date(dataUpdatedAt);

  // Client-only content (a locale timestamp) must stay off the first paint so
  // the hydrated tree matches the server HTML. useClientReady flips after commit.
  const isClient = useClientReady();

  // State for search
  const [searchQuery, setSearchQuery] = useState("");

  // State for active filters
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    industries: [],
    models: [],
    useCaseTypes: [],
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const refreshUseCases = () => {
    void refetch();
  };

  // Filtered use cases based on active filters and search query
  const filteredUseCases = filterUseCases(useCases, activeFilters, searchQuery);

  // Sort use cases - published first, then by other criteria
  const sortedUseCases = [...filteredUseCases].sort((a, b) => {
    // Always show published use cases first
    if (a.isPublished && !b.isPublished) return -1;
    if (!a.isPublished && b.isPublished) return 1;
    // Then sort by other criteria (currently just keeping original order)
    return 0;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedUseCases.length / itemsPerPage);
  const paginatedUseCases = sortedUseCases.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // If 5 or fewer pages, show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always include first page
      pages.push(1);

      // If current page is 1 or 2, show first 3 pages + ellipsis + last page
      if (currentPage < 3) {
        pages.push(2, 3);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
      // If current page is last or second-to-last, show first page + ellipsis + last 3 pages
      else if (currentPage > totalPages - 2) {
        pages.push("ellipsis");
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      }
      // Otherwise show first page + ellipsis + current-1, current, current+1 + ellipsis + last page
      else {
        pages.push("ellipsis");
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <>
      {/* Search and Filter Section - FIXED POSITION */}
      <div className="sticky top-0 z-30 w-full py-4 bg-white border-b border-gray-200 shadow-sm">
        <div className="container px-4 md:px-6">
          <UseCaseFilter
            industryOptions={industryOptions}
            modelOptions={modelOptions}
            useCaseTypeOptions={useCaseTypeOptions}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>
      </div>

      {/* Results Count & Sort */}
      <section className="w-full py-6 bg-gray-50">
        <div className="container px-4 md:px-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="text-gray-600">
                <span className="text-gray-900 font-bold">
                  {filteredUseCases.length}
                </span>{" "}
                use cases found
                <span className="ml-3 text-xs text-gray-500">
                  {/* Only render timestamp on client */}
                  {isClient &&
                    `Last updated: ${lastRefreshed.toLocaleTimeString()}`}
                  {isLoading && " (refreshing...)"}
                  <button
                    onClick={refreshUseCases}
                    disabled={isLoading}
                    className="ml-2 text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  >
                    ↻
                  </button>
                </span>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select className="bg-white border border-gray-300 rounded-md text-gray-900 py-1 pl-3 pr-8 text-sm focus:ring-blue-500 focus:border-blue-500">
                <option>Newest first</option>
                <option>Highest ROI</option>
                <option>Implementation time</option>
                <option>Complexity (lowest first)</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="w-full py-8 md:py-12 bg-gray-50">
        <div className="container px-4 md:px-6">
          {filteredUseCases.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {paginatedUseCases.map((useCase) => (
                <UseCaseCard
                  key={useCase.id}
                  id={useCase.id}
                  title={
                    useCase.title + (useCase.isPublished ? "" : " (Draft)")
                  }
                  description={useCase.description}
                  categories={useCase.categories}
                  models={useCase.models}
                  readTime={useCase.readTime}
                  image={useCase.image}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No matching use cases found
              </h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your filters or search query
              </p>
              <Button
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  setActiveFilters({
                    industries: [],
                    models: [],
                    useCaseTypes: [],
                  });
                  setSearchQuery("");
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8 md:mt-12">
              <nav className="flex items-center space-x-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>

                {getPageNumbers().map((page, index) =>
                  page === "ellipsis" ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 py-1 text-gray-500"
                    >
                      ...
                    </span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="icon"
                      onClick={() => handlePageChange(page as number)}
                      className={
                        currentPage === page
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }
                    >
                      {page}
                    </Button>
                  ),
                )}

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </nav>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
